export function parseMMDDYYYYToDate(value) {
	if (!value) return null;
	if (value instanceof Date)
		return new Date(value.getFullYear(), value.getMonth(), value.getDate());
	if (typeof value !== 'string') return null;
	const parts = value.split('/');
	if (parts.length !== 3) return null;
	const mm = Number.parseInt(parts[0], 10);
	const dd = Number.parseInt(parts[1], 10);
	const yyyy = Number.parseInt(parts[2], 10);
	const d = new Date(yyyy, mm - 1, dd);
	return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateToMMDDYYYY(d) {
	if (!d) return '';
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	const yyyy = d.getFullYear();
	return `${mm}/${dd}/${yyyy}`;
}

// Minimal helpers: normalize a single value or an array to Date(s) at midnight.
export function normalizeDate(value, today) {
	if (!value) return null;
	if (value instanceof Date)
		return new Date(value.getFullYear(), value.getMonth(), value.getDate());
	if (typeof value === 'string') return parseMMDDYYYYToDate(value);
	if (typeof value === 'number' && today) {
		const base = new Date(
			today.getFullYear(),
			today.getMonth(),
			today.getDate(),
		);
		const tmp = new Date(base);
		tmp.setDate(tmp.getDate() + value);
		return new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate());
	}
	return null;
}

export function normalizeDates(arr) {
	if (!arr) return [];
	if (!Array.isArray(arr)) return [];
	// if items are already Date, normalize them; else try to parse strings
	if (arr.length > 0 && arr[0] instanceof Date) {
		return arr.map(
			(d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()),
		);
	}
	return arr.map((s) => parseMMDDYYYYToDate(s)).filter(Boolean);
}

export function formatDatePretty(value) {
	try {
		const parts = ('' + value)
			.split('/')
			.map((p) => Number.parseInt(p, 10));
		if (parts.length !== 3) return 'Select a day...';
		const [mm, dd, yyyy] = parts;
		const d = new Date(yyyy, mm - 1, dd);
		const day = d.getDate();
		const month = d.toLocaleString('en-US', { month: 'long' });
		return `${month} ${day}, ${d.getFullYear()}`;
	} catch {
		return 'Select a day...';
	}
}

const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

/**
 * Formats an ISO date string (YYYY-MM-DD) to "Month D, YYYY" format.
 * @param {string} isoDateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date like "April 8, 2023" or empty string if invalid
 */
export function formatISODateToReview(isoDateString) {
	if (!isoDateString || typeof isoDateString !== 'string') return '';

	const parts = isoDateString.split('-');
	if (parts.length !== 3) return '';

	const year = parseInt(parts[0], 10);
	const month = parseInt(parts[1], 10) - 1;
	const day = parseInt(parts[2], 10);

	if (isNaN(year) || isNaN(month) || isNaN(day)) return '';
	if (month < 0 || month > 11) return '';

	return `${MONTH_NAMES[month]} ${day}, ${year}`;
}

/**
 * Formats a Unix timestamp to a date string in UTC.
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {string} format - Output format: 'iso' for YYYY-MM-DD, 'review' for "Month D, YYYY"
 * @returns {string|undefined} Formatted date string or undefined if invalid
 */
export function formatTimestamp(timestamp, format = 'iso') {
	if (!timestamp) return format === 'iso' ? undefined : '';

	const date = new Date(timestamp * 1000);
	if (Number.isNaN(date.getTime())) return format === 'iso' ? undefined : '';

	const year = date.getUTCFullYear();
	const month = date.getUTCMonth();
	const day = date.getUTCDate();

	if (format === 'review') {
		return `${MONTH_NAMES[month]} ${day}, ${year}`;
	}

	// Default: ISO format (YYYY-MM-DD)
	const monthStr = String(month + 1).padStart(2, '0');
	const dayStr = String(day).padStart(2, '0');
	return `${year}-${monthStr}-${dayStr}`;
}

/**
 * Formats a Unix timestamp to a localized date string.
 * NOTE: This function only accepts timestamps in SECONDS (not milliseconds).
 * Passing a millisecond timestamp will produce an incorrect date.
 * Use formatTimestampToISO if you need automatic seconds/milliseconds detection.
 * @param {number} timestamp - Unix timestamp in seconds (e.g., 1705276800)
 * @returns {string} Formatted date string (e.g., "January 15, 2024") or empty string if invalid
 */
export function formatTimestampToLocaleDate(timestamp) {
	if (!timestamp) return '';
	const date = new Date(timestamp * 1000);
	if (Number.isNaN(date.getTime())) return '';
	const options = { year: 'numeric', month: 'long', day: 'numeric' };
	return date.toLocaleDateString('en-US', options);
}

/**
 * Formats a timestamp to ISO date string (YYYY-MM-DD).
 * Handles both seconds and milliseconds timestamps.
 * @param {number} timestamp - Unix timestamp in seconds or milliseconds
 * @returns {string|undefined} ISO date string or undefined if invalid
 */
export function formatTimestampToISO(timestamp) {
	if (!timestamp) return undefined;
	const date = new Date(
		typeof timestamp === 'number' && timestamp < 1e12
			? timestamp * 1000
			: timestamp,
	);
	if (Number.isNaN(date.getTime())) return undefined;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}
